import { ELocale } from "@rentiohq/shared-frontend/dist/types/i18n.types";
import {
  getLocale,
  getLocalizedText,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import connectWord from "../../../../assets/images/rentio-connect/connect-word.png";
import insightsDrinks from "../../../../assets/images/rentio-connect/insights-drinks.png";
import stickerImage from "../../../../assets/images/rentio-connect/sticker.png";
import fullLogo from "../../../../assets/svg/full-logo.svg";
import * as S from "./RentioConnectBanner.styles";

interface IProps {
  invitationUrl: string;
}

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3"
      y="5"
      width="18"
      height="16"
      rx="3"
      stroke="currentColor"
      strokeWidth="2"
    />
    <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" />
    <circle cx="8" cy="14" r="1.2" fill="currentColor" />
    <circle cx="12" cy="14" r="1.2" fill="currentColor" />
    <circle cx="16" cy="14" r="1.2" fill="currentColor" />
    <circle cx="8" cy="17.5" r="1.2" fill="currentColor" />
    <circle cx="12" cy="17.5" r="1.2" fill="currentColor" />
  </svg>
);

const PinIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M12 22s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12z"
      fill="currentColor"
    />
    <circle cx="12" cy="10" r="2.6" fill="#ffffff" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12h14M13 6l6 6-6 6"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// The blobs follow the shapes of the design: a bean at the top left, two
// crossing strokes at the bottom left and a chevron at the right edge.
const BlobGradient = ({ id }: { id: string }) => (
  <defs>
    <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stopColor={S.COLORS.green} />
      <stop offset="1" stopColor={S.COLORS.greenDark} />
    </linearGradient>
  </defs>
);

export const RentioConnectBanner = (props: IProps) => {
  const { invitationUrl } = props;

  const title = getLocalizedText("rentio_connect_card.title");
  const doNotMiss = getLocalizedText("rentio_connect_card.do_not_miss");

  // The sticker is hand-lettered Dutch; the other languages get the same
  // words as text.
  const stickerMarkup =
    getLocale() === ELocale.NL ? (
      <S.Sticker src={stickerImage} alt={doNotMiss} />
    ) : (
      <S.StickerText>{doNotMiss}</S.StickerText>
    );

  return (
    <S.Banner aria-label={title}>
      <S.BlobTopLeft
        viewBox="0 0 300 220"
        delay="0s"
        duration="14s"
        aria-hidden="true"
      >
        <BlobGradient id="rentio-connect-blob-tl" />
        <path
          d="M40 20C120 -10 230 20 250 90C265 145 200 185 130 170C70 158 5 120 10 70C12 48 22 27 40 20Z"
          fill="url(#rentio-connect-blob-tl)"
        />
      </S.BlobTopLeft>
      <S.BlobBottomLeft
        viewBox="0 0 260 240"
        delay="-5s"
        duration="16s"
        aria-hidden="true"
      >
        <BlobGradient id="rentio-connect-blob-bl" />
        <g
          stroke="url(#rentio-connect-blob-bl)"
          strokeWidth="46"
          strokeLinecap="round"
          fill="none"
        >
          <path d="M30 190L200 60" />
          <path d="M40 60L190 200" />
        </g>
      </S.BlobBottomLeft>
      <S.BlobRight
        viewBox="0 0 260 320"
        delay="-9s"
        duration="18s"
        aria-hidden="true"
      >
        <BlobGradient id="rentio-connect-blob-r" />
        <path
          d="M150 60L245 160L150 260"
          stroke="url(#rentio-connect-blob-r)"
          strokeWidth="58"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </S.BlobRight>

      <S.Lockup role="img" aria-label={title}>
        <S.LockupLogo src={fullLogo} alt="" />
        <S.LockupConnect src={connectWord} alt="" />
        <S.LockupTagline src={insightsDrinks} alt="" />
      </S.Lockup>

      <S.Info>
        <S.InfoRow>
          <S.Badge tint={S.COLORS.mint} color={S.COLORS.ink}>
            <CalendarIcon />
          </S.Badge>
          <div>
            <S.Label>{getLocalizedText("rentio_connect_card.day")}</S.Label>
            <S.Value>{getLocalizedText("rentio_connect_card.date")}</S.Value>
            <S.Sub>{getLocalizedText("rentio_connect_card.time")}</S.Sub>
          </div>
        </S.InfoRow>
        <S.InfoRow>
          <S.Badge tint={S.COLORS.peach} color={S.COLORS.orange}>
            <PinIcon />
          </S.Badge>
          <div>
            <S.Label>
              {getLocalizedText("rentio_connect_card.location_label")}
            </S.Label>
            <S.Value>{getLocalizedText("rentio_connect_card.venue")}</S.Value>
            <S.Sub>{getLocalizedText("rentio_connect_card.city")}</S.Sub>
          </div>
        </S.InfoRow>
      </S.Info>

      <S.Divider />

      <S.Cta>
        {stickerMarkup}
        <S.Button
          href={invitationUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {getLocalizedText("rentio_connect_card.action")}
          <ArrowIcon />
        </S.Button>
      </S.Cta>
    </S.Banner>
  );
};
